/**
 * Seção das sessões de treinamento AWS Showroom, oferecidas como cortesia.
 *
 * Existe pelo mesmo motivo da seção do assessment: `premissas.sessoesTreinamento` era gravado,
 * validado de 0 a 3 e **nenhum renderizador o consumia**. Escolher sessões no formulário não mudava
 * a proposta.
 *
 * Diferente do assessment, aqui não houve mudança de dado: zero já significava "não incluído" e o
 * schema já limitava a três. O defeito era só a ausência de consumo.
 *
 * O conteúdo descritivo não veio do construtor anterior — o fonte dele não está no repositório
 * (decisão D10), e o inventário do legado registra apenas "sessões de treinamento AWS Showroom (1 a
 * 3), sem preço". O texto abaixo é deliberadamente genérico quanto a entregáveis: prometer gravação,
 * material ou certificado num documento que o cliente assina exigiria confirmação de quem entrega.
 */

import { type DocumentoProposta } from '@emaster/dominio';

import { cabecalhoSecao } from './componentes.ts';
import { escaparHtml } from './escapar.ts';
import { CONTAINER, icone } from './tokens.ts';

interface ItemLista {
  readonly iconeNome: string;
  readonly texto: string;
}

/** Como a sessão acontece. Formato, condução e definição de agenda. */
const COMO_FUNCIONA: readonly ItemLista[] = [
  {
    iconeNome: 'users',
    texto:
      'Conduzidas por arquitetos certificados AWS da eMaster, os mesmos profissionais que sustentam o seu ambiente.',
  },
  {
    iconeNome: 'monitor',
    texto: 'Remotas ou presenciais, conforme a preferência e a distribuição do seu time.',
  },
  {
    iconeNome: 'calendar-clock',
    texto: 'Agendadas com o seu time após a aprovação desta proposta, na data que for conveniente.',
  },
  {
    iconeNome: 'target',
    texto:
      'A pauta é definida com você e parte dos serviços desta proposta, não de um roteiro genérico.',
  },
];

/**
 * Temas que o cliente pode escolher.
 *
 * Ficam como escolha, e não como programa fechado: a pauta é acordada, e listar um currículo fixo
 * criaria expectativa de conteúdo que talvez não sirva ao time do cliente.
 */
const TEMAS: readonly string[] = [
  'Fundamentos da nuvem AWS e os serviços que compõem esta proposta',
  'Boas práticas de identidade e acesso, com IAM e autenticação em múltiplos fatores',
  'Gestão e previsibilidade de custos na AWS',
  'Monitoramento, alertas e leitura de métricas do ambiente',
  'Backup, recuperação e continuidade de operação',
  'Rotinas de operação do dia a dia no console e na linha de comando',
];

/** Rótulo com a contagem, já concordado. */
export function rotuloSessoes(quantidade: number): string {
  return quantidade === 1 ? '1 sessão' : `${String(quantidade)} sessões`;
}

/**
 * Renderiza a seção de treinamento.
 *
 * @returns HTML da seção, ou string vazia quando a proposta não inclui sessões.
 */
export function secaoTreinamento(doc: DocumentoProposta): string {
  const sessoes = doc.premissas.sessoesTreinamento;
  if (sessoes <= 0) return '';

  const comoFunciona = COMO_FUNCIONA.map(
    (i) => `<li class="flex items-start gap-3">
                            <span class="mt-0.5 shrink-0 text-brand-accent">${icone(i.iconeNome, 'w-5 h-5')}</span>
                            <span class="text-sm leading-relaxed text-gray-600">${escaparHtml(i.texto)}</span>
                        </li>`,
  ).join('\n                        ');

  const temas = TEMAS.map(
    (t) => `<li class="flex items-start gap-3">
                            <span class="mt-0.5 shrink-0 text-brand-accent">${icone('check', 'w-5 h-5')}</span>
                            <span class="text-sm leading-relaxed text-white/90">${escaparHtml(t)}</span>
                        </li>`,
  ).join('\n                        ');

  return `
    <section id="treinamento" class="scroll-mt-24 border-t border-gray-200 bg-gray-50 py-20">
        <div class="${CONTAINER}">
            ${cabecalhoSecao('Cortesia', 'Sessões de treinamento AWS Showroom', [
              'Ambiente bem construído que ninguém sabe operar continua gerando chamado. As sessões existem para que o seu time use o que foi entregue, com quem construiu.',
            ])}

            <div class="mb-8 flex flex-wrap items-center gap-3">
                <span class="inline-flex items-center gap-2 rounded-full bg-brand-accent/10 px-4 py-2 text-sm font-bold text-brand-accent">
                    ${icone('gift', 'w-4 h-4')} ${escaparHtml(rotuloSessoes(sessoes))} sem custo nesta proposta
                </span>
                <span class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-brand-blue">
                    ${icone('graduation-cap', 'w-4 h-4')} Capacitação do seu time
                </span>
            </div>

            <div class="grid gap-8 lg:grid-cols-5">
                <div class="lg:col-span-3">
                    <div class="rounded-3xl border border-gray-100 bg-white p-8">
                        <h3 class="mb-5 text-lg font-bold text-brand-blue">Como funciona</h3>
                        <ul class="space-y-4">
                            ${comoFunciona}
                        </ul>
                    </div>
                </div>

                <div class="lg:col-span-2">
                    <div class="rounded-3xl bg-brand-blue p-8">
                        <h3 class="mb-5 text-lg font-bold text-white">Temas que você pode escolher</h3>
                        <ul class="space-y-4">
                            ${temas}
                        </ul>
                        <p class="mt-6 border-t border-white/15 pt-4 text-xs leading-relaxed text-white/70">
                            A pauta de cada sessão é acordada com o seu time antes da realização.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>`;
}
